import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { nth, reduce, isEmpty, keys, map } from 'lodash'
import cn from 'classnames'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { toast } from 'react-toastify'

import useNestedSelection from '../../../hooks/useNestedSelection'
import { centsToDollars } from '../../../utils/formatters'

import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import BackChevron from '../../BackChevron'

import { ReactComponent as ChevronDown } from  '../../../assets/icons/tabs/chevron-down.svg'
import { ReactComponent as Checkmark } from '../../../assets/icons/checkmark-icon.svg'
import { ReactComponent as Minus } from '../../../assets/icons/minus.svg'

import ToggleButton from '../../ToggleButton'

import styles from './SplitOrder.module.scss'

const SelectionIcon = ({ active, icon, onClick }) => {
  const renderedIcon = active ? icon : ''

  return (
    <span onClick={onClick} className={cn('selectionIcon', styles.selectionIcon, { [styles.active]: active })}>{renderedIcon}</span>
  )
}

const ExpandableList = ({ items, itemCount, allSelected, selections, onSelectItem, onSelectGroup, onSelectAll }) => {
  const [expandedItems, setExpandedItems] = useState({})

  const onToggleItem = (identifier, index) => {
    onSelectItem(identifier, index)
  }

  const onToggleGroup = (identifier, indexes) => {
    onSelectGroup(identifier, indexes)
  }

  const onExpandGroup = (identifier) => {
    const clone = { ...expandedItems }

    clone[identifier] = !clone[identifier]

    setExpandedItems(clone)
  }

  const onSelectAllItems = () => {
    if (allSelected) {
      onSelectAll({})
    } else {
      const selections = keys(items).reduce((accumulator, key) => {
        return {
          ...accumulator,
          [key]: Array.from(Array(items[key].items.length), (_, index) => index)
        }
      }, {})

      onSelectAll(selections)
    }
  }

  const renderItems = () => {
    return keys(items).map((identifier, index) => {
      const group = items[identifier]
      const selectionSize = selections[identifier]?.length ?? 0
      const groupSize = group.items.length
      const singleLineItem = groupSize === 1
      const isExpanded = expandedItems[identifier] ?? false
      const entireGroupSelected = groupSize === selectionSize

      const resolveGroupIcon = () => {
        if (entireGroupSelected) return <Checkmark />
        if (selectionSize > 0) return <Minus />

        return ''
      }

      const handleOnExpandGroup = () => {
        if (!singleLineItem) {
          onExpandGroup(identifier)
        }
      }

      return (
        <div className={styles.group} key={identifier}>
          <div className={cn(styles.groupHeader, { [styles.selected]: entireGroupSelected })}>
            <ChevronDown onClick={handleOnExpandGroup} className={cn(styles.expandButton, { [styles.flip]: isExpanded, [styles.hide]: singleLineItem })} />
            <span>{group.quantity}</span>
            <span className={styles.groupName}>
              <span>{group.name}</span>
              {group.modifiersString && <span className={styles.modifiers}>{group.modifiersString}</span>}
            </span>
            <span className={styles.price}>{group.price}</span>
            <SelectionIcon active={selectionSize > 0} icon={resolveGroupIcon()} onClick={() => onToggleGroup(identifier, Array.from(Array(group.items.length), (_, index) => index))} />
          </div>
          {isExpanded && <ul className={styles.groupItems}>
            {group.items.map((item, index) => {
              const checked = selections?.[identifier]?.includes?.(index) ?? false

              return (
                <li key={item.lineItemUuid} className={cn(styles.groupItem, { [styles.selected]: checked })} onClick={() => onToggleItem(identifier, index)}>
                  <span className={styles.itemName}>
                    <span>{item.name}</span>
                    {item.modifiersString && <span className={styles.modifiers}>{item.modifiersString}</span>}
                  </span>
                  <span className={styles.price}>{item.price}</span>
                  <SelectionIcon active={checked} icon={<Checkmark />} />
                </li>
              )
            })}
          </ul>}
        </div>
      )
    })
  }

  return (
    <div className={styles.groupedItems}>
      <div className={styles.selectAll}>
        <span>All Items ({itemCount})</span>
        <ToggleButton checked={allSelected} onChange={onSelectAllItems} />
      </div>
      {renderItems()}
    </div>
  )
}

const getSelectedSplitOrderItems = (selectedItemIndexes, items) => {
  return map(selectedItemIndexes, (index) => nth(items, index))
}

const SplitOrderByItems = ({
  className,
  exemptions,
  itemGroupings,
  items,
  onClose,
  onConfirmItems,
  onPayNow,
  orderId,
  orderNumber,
  suiteInformation,
}) => {
  const classNames = cn(styles.splitOrder, className)
  const history = useHistory()
  const [subtotal, setSubtotal] = useState(0)
  const [valid, setValid] = useState(false)
  const [selectedItemIndexes, setSelectedItemIndexes] = useState([])
  const [selections, setSelection, setGroupSelection, setAllSelection] = useNestedSelection()
  const formattedSubtotal = centsToDollars(subtotal)

  const calculateSubtotal = () => {
    const [indexes, subtotal] = keys(selections).reduce(([indexes, subtotal], group) => {
      const groupInstance = selections[group] ?? []

      groupInstance.forEach((index) => {
        const item = nth(itemGroupings[group].items, index)
        const modifiersSubtotal = reduce(item?.modifiers, (acc, modifier) => {
          return acc + modifier?.defaultPriceInCents ?? 0
        }, 0)

        indexes.push(item?.originalIndex)
        subtotal += item?.priceInCents + modifiersSubtotal
      })

      return [indexes, subtotal]
    }, [[], 0])

    setSelectedItemIndexes(indexes)
    setSubtotal(subtotal)
    setValid(!isEmpty(selectedItemIndexes) && selectedItemIndexes.length < items.length)
  }

  const presentError = () => {
    if (isEmpty(selectedItemIndexes)) {
      toast.error("You must make a selection before proceeding.", { autoClose: 2500 })
    } else if (selectedItemIndexes.length >= items.length) {
      toast.error("You can't select every item.", { autoClose: 2500 })
    }
  }

  const handleConfirmItems = () => {
    if (!valid) {
      presentError()
    } else {
      onConfirmItems(selectedItemIndexes, getSelectedSplitOrderItems(selectedItemIndexes, items))
    }
  }

  useDeepCompareEffect(() => {
    calculateSubtotal()
  }, [selections, selectedItemIndexes])

  return (
    <SidebarModal
      show
      title="Split Order"
      onClose={onClose}
      leftAccessory={<BackChevron onClick={() => history?.replace?.(`/tabs/${orderId}`)} />}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <div className={styles.splitOrderHeader}>
          <p>{suiteInformation?.displayName}: #{orderNumber}</p>
          <p>Subtotal: <span className={styles.subtotal}>{formattedSubtotal}</span></p>
        </div>
        <ExpandableList itemCount={items.length} allSelected={selectedItemIndexes.length === items.length} items={itemGroupings} selections={selections} onSelectItem={setSelection} onSelectGroup={setGroupSelection} onSelectAll={setAllSelection} />
        <div className={styles.buttonContainer}>
          <Button className={cn(styles.actionButton, styles.mintGreenHighlight, { disabled: !valid })} onClick={handleConfirmItems}>Confirm Items</Button>
        </div>
      </div>
    </SidebarModal>
  )
}

export default SplitOrderByItems
